<template>
<zm-dialog :visible.sync="visible" class="comparison-dialog comparison-dialog--zoom-one" aria-labelledby="zoomMeetingFullComparisonTableTitle" show-close @close="handleCancel">
  <h1 class="comparison-dialog__title" id="zoomMeetingFullComparisonTableTitle" slot="title">{{ $t('marketing.new_pricing.full_plan') }}</h1>
  <component :is="$context.is.desktop ? 'PlanComparison' : 'PlanComparisonMobile'" class="opc-plan-comparison" :treeData="treeData" :colConfig="colConfig" tableRowClassName="opc-comparison-table">
    <template v-slot:[buttonSlotName]="{ col }">
        <div v-if="$context.is.desktop" tabindex="-1" class="type-title">{{col.title}}</div>
        <template v-if="!col.hideBtn">
          <i tabindex="-1" v-if="col.category === currentBaseCategory || col.selected" class="zm-icon-ok no-event-btn" :aria-label="col.title + ' Selected'"></i>
          <zm-tooltip v-else-if="col.btn && col.btn.disabledTips" :disabled="!col.btn.disabled">
            <p @click='onSubmitFormClick(col, $event)' slot="content" v-html="col.btn.disabledTips"/>
            <!-- if use div elements to wrap it, there are some problems with the display and hiding of tooltip on mobile. -->
            <!-- <div> -->
              <zm-button :type="col.btn.type" round @click="col.btn.callback(col)" :disabled="col.btn.disabled" :aria-label="col.btn.ariaLabel">{{ col.btn.label }}</zm-button>
            <!-- </div> -->
          </zm-tooltip>
          <zm-button tabindex="-1" v-else-if="col.btn" :type="col.btn.type" round @click="col.btn.callback(col)" :disabled="col.btn.disabled" :aria-label="col.btn.ariaLabel">{{ col.btn.label }}</zm-button>
          <div v-else class="empty-btn" aria-hidden="true" />
        </template>
      </template>
  </component>
</zm-dialog>
</template>
<script type="text/jsx">
import { get, groupBy } from 'lodash';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import {
  AUDIO_SUB_TYPES,
  BILLING_CYCLE,
  contactUsPath,
  planTypes,
  BASE_PLAN_SUB_TYPES, ZOOM_MEETING_PRO_MIN_LICENSE, BIZ_PLUS_PLAN_CODES_SUPPORTED,
  PBX_CATEGORYS,
} from '@/common/constants';
import { flowTypes } from '@/common/service/page-config';
import { ZOOM_MEETING_SUB_TYPES, isBizPlusContext, checkBizPlanIsPending } from '../common/utils';
import { ZoomMeetingComparisonDataMixins } from '@/components/full-comparison-table/comparison-data-mixins';
import { translation } from '@/plugins/i18n';
import { isEmptyVal } from '@/utils';
import { plansof } from '@/utils/chain';
import { $query } from '@/global-install';
import { getPlanTypeByCategory } from '@/common/service/parse';
import MeetingComparisonMixins from './meeting-comparison-mixins'
import {ONLINE_NOT_SUPPORT_BILLING_CYCLE} from "@/common/service/non-standard-billing-cycle-utils";
import {
  computedPbxAddonPlans,
  computedPbxBasePlans, computedPbxCallingPlans, computedPbxFeaturePlans,
  computedPbxNonBasePlans,
} from '@/common/service/computers/plans/pbx';
import { getMigrateFlowType, migrationPlanTypes } from '@/plans/ZOOM_MEETING/dialog/migration-plan';

const PRO2BIZ_ZONE_BUNDLE = 'pro2zone_bundle'; // meeting pro --> zoom one biz/zoom one biz plus
const BIZ2BIZ_ZONE_BUNDLE = 'biz2zone_bundle'; // meeting biz --> zoom one biz plus

const getDefaultStatusMap = () => {
  return {
    disabled: true,
    link: contactUsPath,
    disabledTips: translation('billing.error.can_not_bought_plan_tips'),
  }
}

const hostCache = {};

export default {
  name: 'FullComparisonDialog',
  mixins: [ConfirmComponent, ZoomMeetingComparisonDataMixins, MeetingComparisonMixins],
  data() {
    return {
      visible: false,
    };
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
      allPlanMap: state => state.ShoppingCart.allPlanMap
    }),
    ...mapGetters([
      'getCurrentBasePlanInfo',
      'getSelectedPlans',
      'getBoughtBasePlanInfo',
      'basicConfig',
      'boughtPlans',
      'boughtPlansGroupByCategoryCode',
      'canUpgradeZoneBundle',
      'partnerRefer',
      'freeTrialPlans',
      'limitLicenseWithBaseZoneTypes',
      'plansofBizBundle',
      'currentEditPlan',
      'migratePlanState',
      'plancodeMapSubscriptionDisabled',
      'getTypeGroupSelectedPlans'
    ]),
    buttonSlotName() {
      return this.$context.is.desktop ? 'comparison-table-header' : 'button';
    },
    haveZoomUnited() {
      return !!this.boughtPlans.find(item => item.type === planTypes.ZOOM_UNITED);
    },
    canBuyBizPlus() {
      const disablePlanCategoryList = get(this.allPlanMap, 'disablePlanCategoryList', []);
      return !!get(this.allPlanMap, 'zoomone.zoom-one-biz-plus', '') && !disablePlanCategoryList.includes('ZONEBIZP');
    },
    colConfig() {
      const checkIsSelected = (val) => this.currentBaseCategory === val;

      const configList = [
        {
          title: this.$t('common.pro'),
          key: 'pro',
          category: ZOOM_MEETING_SUB_TYPES.PRO,
          visible: true,
          isSelected: checkIsSelected(ZOOM_MEETING_SUB_TYPES.PRO),
          btn: {
            label: this.$t('billing.common.select'),
            type: 'primary',
            ariaLabel: 'Select Pro',
            ...this.configProBtnStatus,
            callback: this.handleSwitchMeetingPlan.bind(this),
          },
        },
        {
          title: this.$t('common.business'),
          category: ZOOM_MEETING_SUB_TYPES.BIZ,
          key: 'business',
          visible: true,
          selected: plansof(this.getSelectedPlans).type(planTypes.ZOOM_MEETING).category(ZOOM_MEETING_SUB_TYPES.BIZ).has(),
          isSelected: checkIsSelected(ZOOM_MEETING_SUB_TYPES.BIZ),
          btn: {
            label: this.$t('billing.common.select'),
            ariaLabel: 'Select Business',
            type: 'primary',
            ...this.configBizBtnStatus,
            callback: this.handleSwitchMeetingPlan.bind(this),
          },
        },
        {
          title: this.$t('one.bizPlus'),
          category: ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS,
          key: 'businessPlus',
          visible: this.configBizPlusVisible,
          selected: plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).has(),
          isSelected: checkIsSelected(ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS),
          btn: {
            label: this.$t('billing.common.select'),
            ariaLabel: 'Select Business Plus',
            type: 'primary',
            ...this.configBizPlusBtnStatus,
            callback: this.handleSwitchMeetingPlan.bind(this),
            // biz plus is currently not supported on mobile, remove this after subsequent support
            ...(this.$context.is.desktop ? {} : { disabled: true }),
          },
        },
      ];

      return configList.filter((p) => p.visible);
    },
    configBizPlusVisible() {
      // if this is for partner refer case, don't show bizplus plan
      if (!isEmptyVal(this.partnerRefer)) {
        return false;
      }

      return true;
    },
    currentBaseCategory() {
      // biz plus to biz
      if(isBizPlusContext && !this.migratePlanState.is.bundle2biz) return ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS;
      const { planType, plans } = this.getCurrentBasePlanInfo;
      if(planType !== planTypes.ZOOM_MEETING || !plans[0]) {
        return '';
      }

      return get(plans, '0.zoomMeetingCategory', '');
    },
    boughtBaseCategory() {
      const { plans = [] } = this.getBoughtBasePlanInfo;
      return get(plans, '0.zoomMeetingCategory', '');
    },
    isBulkLicenseAccountBase() {
      return this.basicConfig?.isBulkLicenseAccount && this.getBoughtBasePlanInfo?.planType === planTypes.ZOOM_MEETING;
    },
    hasUnupportBillingCyclePlan() {
      const { plans = [] } = this.getBoughtBasePlanInfo;
      return ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(get(plans.filter(p => p.type === planTypes.ZOOM_MEETING), '0.billing_cycle', ''));
    },
    bizCanDowngrade() {
      // edit flow and current edit business plan
      if (flowTypes.edit === this.$context.flow
        && [ZOOM_MEETING_SUB_TYPES.BIZ, ZOOM_MEETING_SUB_TYPES.BIZ_INR].includes(this.boughtBaseCategory)
      ) {
        // bought unlimited audio plan
        const isBoughtUnlimitedAudio = Object.keys(this.boughtPlansGroupByCategoryCode).some((category) => {
          return [AUDIO_SUB_TYPES.UNLIMITED, AUDIO_SUB_TYPES.UNLIMITED].includes(category);
        });

        // have bought zoom one biz plus plan
        const boughtZoneBundlePlans = this.boughtPlans?.filter((plan) => plan?.type === planTypes.ZOOM_ONE_BUNDLE);

        // have bought zoom one biz plus free trial plan
        const boughtZoneBundleFtPlans = this.freeTrialPlans?.filter((plan) => plan?.type === planTypes.ZOOM_ONE_BUNDLE);

        // is allow downSell
        const disableDownSell = this.basicConfig?.downSellData?.disableDownSell;

        return (
          !boughtZoneBundlePlans?.length &&
          !boughtZoneBundleFtPlans?.length &&
          !isBoughtUnlimitedAudio &&
          !disableDownSell &&
          !this.isBulkLicenseAccountBase
        );
      }

      return false;
    },
    configProBtnStatus() {
      const flowType = this.$context.flow;
      const defaultStatusMap = getDefaultStatusMap();
      if(this.haveZoomUnited) {
        return {
          disabled: true,
          link: '/',
        }
      }

      if ([flowTypes.basic2pro, flowTypes.buyNow, flowTypes.upgrade, flowTypes.trial2paid].includes(flowType)) {
        return {
          disabled: false,
          link: '/'
        }
      }

      if ([flowTypes.add2cart].includes(flowType) && this.basicConfig?.bizCanDowngrade) {
         // if no meeting plan which means purchased standalone product as paid account
         // and no biz+ plan
        if (!this.meetingPlans.length && !this.plansofBizBundle.boughtBizPlusPlan.length) {
          return {
            disabled: false,
            link: `/opc/buy?plan=z1s&subPlan=newsubs&from_sub=pro&type=add&majorPlanHost=1`
          }
        }
        // for biz plus
        if (isBizPlusContext) {
          return defaultStatusMap;
        }
        if (this.basicConfig?.bizCanDowngrade) {
          return {
            disabled: false,
            link: '/'
          };
        }
      }

      // MIX_BUY
      if ([flowTypes.edit, flowTypes.pro2biz, flowTypes.mixBuy].includes(flowType)) {
        const boughtBaseCategory = this.boughtBaseCategory;

        // for edit pro or biz can downgrade to pro
        if (boughtBaseCategory === ZOOM_MEETING_SUB_TYPES.PRO ||
          (boughtBaseCategory === ZOOM_MEETING_SUB_TYPES.BIZ && this.bizCanDowngrade)
        ) {
          return {
            disabled: false,
            link: '/'
          };
        }
      }

      return defaultStatusMap;
    },
    configBizBtnStatus() {
      const defaultStatusMap = getDefaultStatusMap();
      const flowType = this.$context.flow;

      if(this.haveZoomUnited) {
        return {
          disabled: true,
          link: '/',
        }
      }

      // for biz plus
      if ([flowTypes.add2cart].includes(flowType)) {
        // if no meeting plan which means purchased standalone product as paid account
        if (!this.meetingPlans.length) {
          return {
            disabled: false,
            link: `/opc/buy?plan=z1s&subPlan=newsubs&from_sub=biz&type=add&majorPlanHost=1`
          }
        }
        if (isBizPlusContext) {
          return {
            disabled: true,
            link: '/',
          };
        }
      }

      if ([flowTypes.basic2pro, flowTypes.buyNow, flowTypes.upgrade, flowTypes.trial2paid, flowTypes.add2cart].includes(flowType)) {
        return {
          disabled: false,
          link: '/'
        }
      }

      if ([flowTypes.edit].includes(flowType) && isBizPlusContext) {
        // biz+ downgrade to biz
        if (this.basicConfig?.downSellData?.disableDownSell) return defaultStatusMap;
        // if has zp add-on plans and no zp calling plans, can not downgrade
        if (!computedPbxCallingPlans(this.boughtPlans).length && (computedPbxAddonPlans(this.boughtPlans).length || computedPbxFeaturePlans(this.boughtPlans).length)) return defaultStatusMap;
        // if has pending plan, don't allow downgrade to biz
        const { boughtBizPlusPlan, boughtBizPlan, hasFreeTrial } = this.plansofBizBundle;
        const hasPendingBizPlusPlan = boughtBizPlusPlan.find(p => {
          return this.plancodeMapSubscriptionDisabled[p.plan_code] || false;
        });
        if (hasPendingBizPlusPlan) return defaultStatusMap;
        // check if has pending activation/downgrade biz plan
        let hasPendingBizPlan = checkBizPlanIsPending();
        if (hasPendingBizPlan) return defaultStatusMap;
        // check if contains op free trial plan
        if (hasFreeTrial) return defaultStatusMap;
        if (boughtBizPlan.length === 0) {
          return {
            disabled: false,
            link: '/'
          }
        }
        return defaultStatusMap;
      }

      // MIX_BUY
      if ([flowTypes.edit, flowTypes.pro2biz, flowTypes.mixBuy].includes(flowType)) {
        const boughtBaseCategory = this.boughtBaseCategory;
        const zoneCanUpgrade = this.basicConfig?.zoneCanUpgrade;

        // for pro2biz flow or edit business plan
        if ((boughtBaseCategory === ZOOM_MEETING_SUB_TYPES.PRO && zoneCanUpgrade)
          || boughtBaseCategory === ZOOM_MEETING_SUB_TYPES.BIZ) {
          return {
            disabled: false,
            link: '/'
          }
        }
      }

      return defaultStatusMap;
    },
    configBizPlusBtnStatus() {
      const flowType = this.$context.flow;
      const defaultStatusMap = getDefaultStatusMap();

      // not allow to purchase zoom one biz plus
      if (!this.canBuyBizPlus) {
        return defaultStatusMap;
      }

      // const from = getPlanTypeByCategory($query?.from) !== planTypes.ZOOM_MEETING && $query?.from || 'zonebundle_zonebizpzau';

      if (flowTypes.add2cart === flowType) {
        // if no meeting plan which means purchased standalone product as paid account
        if (!this.meetingPlans.length) {
          return {
            disabled: false,
            link: `/opc/buy?plan=z1s&subPlan=newsubs&from_sub=zonebundle&type=add&majorPlanHost=0`
          }
        }
      }

      if (flowTypes.buyNow === flowType) {
        return {
          disabled: false,
          link: `/opc/buy?plan=zonebundle&from=zonebundle_zonebizpzvu&period=annual`,
          // link: `/opc/buy?plan=zonebundle&from=${from}&period=annual`,
        }
      }

      if (flowTypes.basic2pro === flowType) {
        return {
          disabled: false,
          link: `/account/billing/buy?plan=zonebundle&subPlan=allSubPlan&type=basic2pro&from=zonebundle_zonebizpzvu&plan=zonebundle&period=annual&from=zonebundle_zonebizpzvu`,
        }
      }

      if (flowTypes.upgrade === flowType) {
        return defaultStatusMap;
      }

      if (flowTypes.edit === flowType && this.migratePlanState.is.downgrade) {
        return {
          disabled: false,
          link: '/'
        }
      }

      if (flowTypes.edit === flowType && this.canUpgradeZoneBundle) {
        const plan = this.boughtBaseCategory || ZOOM_MEETING_SUB_TYPES.PRO;
        const upgradeZoneMapping = { pro: PRO2BIZ_ZONE_BUNDLE, biz: BIZ2BIZ_ZONE_BUNDLE, education: BIZ2BIZ_ZONE_BUNDLE };

        return {
          disabled: false,
          link: `/account/billing/buy?plan=${plan}&subPlan=none&type=${upgradeZoneMapping[plan]}`,
        }
      }

      return defaultStatusMap;
    },
    meetingPlans() {
      return plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).get();
    }
  },
  methods: {
    ...mapActions(['switchCategoryAction']),
    ...mapMutations(['removePlan', 'updateSelectPlan', 'setMigratePlanState']),
    handleRowClick(row) {
      if (row.subTitle) {
        const featureCol = document.querySelector(`.${row.id}-sub-title`);
        let cel = null;
        let expandBtn = null;
        if (featureCol) {
          cel = featureCol.parentElement;
        }
        if (cel) {
          expandBtn = cel.querySelector('.zm-table__expand-icon');
        }
        if (expandBtn) {
          expandBtn.click();
        }
      }
    },
    arraySpanMethod({ row, columnIndex }) {
      if (row.subTitle) {
        return columnIndex === 0 ? [1, 10] : [0, 0];
      }
    },
    switchProBizUrl(col){
      if(isBizPlusContext){
        const billingCycle = plansof(this.getSelectedPlans).type(planTypes.ZOOM_ONE_BUNDLE).get(0)?.billing_cycle || BILLING_CYCLE.ANNUAL;
        const from = getPlanTypeByCategory($query.from) === planTypes.ZOOM_ONE_BUNDLE ? "" : $query.from;
        location.href = `/opc/buy/config?plan=${col.category}&period=${billingCycle}&from=${from}&type=${$query.type??''}`;
        return Promise.reject();
      }
      return Promise.resolve();
    },
    async loadCache() {
      if (!this.migratePlanState.flow) return;
      // which means switch biz back to biz+
      const hotCache = this.migratePlanState.hotCache;
      const newHotCache = this.getSelectedPlans.map(p => ({ planCode: p.plan_code, type: p.type, cartInfo: { ...p.cartInfo } }));
      this.getSelectedPlans.forEach(p => {
        this.removePlan(p?.plan_code);
      });
      hotCache.forEach(p => {
        this.updateSelectPlan({
          plan_code: p.planCode,
          cartInfo: p.cartInfo
        });
      });
      // refresh plan card
      this.setMigratePlanState({
        planList: [],
        flow: '',
        hotCache: newHotCache
      });
    },
    async bizPlusDowngradeToBiz() {
      // downgrade message
      const { selectedBizPlusPlan, selectedPbxBasePlan, totalLicenseWithoutFreeTrial, maxLicensesAddonPlan, assignedBizPlusLicenseQuantity } = this.plansofBizBundle;
      const maxAddonLicense = maxLicensesAddonPlan?.cartInfo?.selected_number ?? 0;
      const quantity = Math.max(maxAddonLicense, assignedBizPlusLicenseQuantity, totalLicenseWithoutFreeTrial, ZOOM_MEETING_PRO_MIN_LICENSE);

      const planList = selectedBizPlusPlan.map(p => {
        const extraKeys = {}
        if (ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(p.billing_cycle)) {
          Object.assign(extraKeys, { oldPlanType: p.plan_type })
        }
        return {
          oldPlanCode: p.plan_code,
          quantity: p.cartInfo.selected_number,
          ...extraKeys
        }
      });
      const hotCache = [...selectedBizPlusPlan, selectedPbxBasePlan].map(p => ({ planCode: p.plan_code, type: p.type, cartInfo: { ...p.cartInfo } }));
      // remove biz+ plans
      selectedBizPlusPlan.forEach(p => {
        this.removePlan(p.plan_code);
      });
      // remove pbx base plan
      this.removePlan(selectedPbxBasePlan.plan_code);
      // add biz plan to cart
      const billingCycle = selectedBizPlusPlan[0].billing_cycle;
      const bizPlan = this.planMapFlat.find(p => p.billing_cycle === billingCycle && p.type === planTypes.ZOOM_MEETING && p.category === BASE_PLAN_SUB_TYPES.BIZ);
      this.updateSelectPlan({
        plan_code: bizPlan.plan_code,
        cartInfo: { selected_number: quantity }
      });
      // refresh plan card
      this.setMigratePlanState({
        planList,
        hotCache
      });
    },
    async upgradeToBizPlus() {
      const boughtMeetingPlanLicense = plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).get(0)?.cartInfo?.selected_number ?? 0;
      const { selectedBizPlan, boughtBizPlan } = this.plansofBizBundle;
      const bizPlan = selectedBizPlan.length ? selectedBizPlan : boughtBizPlan;
      const quantity = Math.max(bizPlan?.[0]?.cartInfo?.selected_number, boughtMeetingPlanLicense, ZOOM_MEETING_PRO_MIN_LICENSE);
      const planList = bizPlan.map(p => {
        return {
          oldPlanCode: p.plan_code,
          quantity: p.cartInfo.selected_number,
        }
      });
      const oldHotCache = this.migratePlanState.hotCache;
      const hotCache = [...bizPlan].map(p => ({ planCode: p.plan_code, type: p.type, cartInfo: { ...p.cartInfo } }));
      // remove biz plan
      bizPlan.forEach(p => {
        this.removePlan(p.plan_code);
      });
      // load cache
      if (oldHotCache.length) {
        oldHotCache.forEach(p => {
          this.updateSelectPlan({
            plan_code: p.planCode,
            cartInfo: { selected_number: p.type === planTypes.ZOOM_ONE_BUNDLE ? quantity : p.cartInfo.selected_number }
          });
        });
      } else {
        const boughtPbxBasePlans = computedPbxBasePlans(this.boughtPlans);
        const boughtPbxPlans = computedPbxNonBasePlans(this.boughtPlans);
        const boughtMeetingPlan = plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).get(0);
        let billingCycle =  boughtMeetingPlan.billing_cycle || BILLING_CYCLE.ANNUAL;

        if (boughtPbxBasePlans.length && boughtPbxPlans.length) {
          billingCycle = boughtPbxPlans?.[0]?.billing_cycle;
        }

        // add biz plus plans to cart
        const bizPlusPlan = this.planMapFlat.find((x) => x.billing_cycle === billingCycle && x.type === planTypes.ZOOM_ONE_BUNDLE && BIZ_PLUS_PLAN_CODES_SUPPORTED.includes(x.plan_code));
        let pbxBasePlan = boughtPbxBasePlans?.[0] || computedPbxBasePlans(this.planMapFlat).find(p => p.category === PBX_CATEGORYS.ZVP);
        // biz plus
        this.updateSelectPlan({
          plan_code: bizPlusPlan.plan_code,
          cartInfo: { selected_number: quantity }
        });
        // pbx base plan
        this.updateSelectPlan({
          plan_code: pbxBasePlan.plan_code,
          cartInfo: { selected_number: 1 }
        });
      }
      // refresh plan card
      this.setMigratePlanState({
        planList,
        hotCache
      });
    },
    async handleSwitchMeetingPlan(col) {
      if (col?.btn?.disabled) return;

      // current edit plan
      if (this.$context.is.edit) {
        const currentEditPlan = (this.getTypeGroupSelectedPlans?.[planTypes.ZOOM_MEETING] || this.getTypeGroupSelectedPlans?.[planTypes.ZOOM_ONE_BUNDLE])?.[0] ?? {};
        const originalEditPlan = this.currentEditPlan;
        const currentEditPlanCategory = currentEditPlan.zoomMeetingCategory;
        const originEditPlanCategory = originalEditPlan.zoomMeetingCategory;
        const migrateType = migrationPlanTypes({
          originEditPlanCategory, currentEditPlanCategory, nextEditPlanCategory: col.category
        });
        const action = this.getFnByMigrateTypes();
        await action[migrateType].call(this, { col, currentEditPlanCategory, migrateType });
        this.setMigratePlanState({
          migrateType,
          flow: getMigrateFlowType(migrateType)
        });
        console.log(migrateType);
        this.handleSure();
        return Promise.reject();
      }

      if (this.$context.is.add2cart) {
        if (isBizPlusContext || col.category === BASE_PLAN_SUB_TYPES.ZOOM_ONE_BIZ_PLUS) {
          location.replace(col.btn.link);
          return;
        }
      }

      await this.switchProBizUrl(col);

      this.$tracking.buyInteraction('product details', `full plan comparison select - ${col?.category}`);

      switch(col.category) {
        case ZOOM_MEETING_SUB_TYPES.PRO:
        case ZOOM_MEETING_SUB_TYPES.BIZ:
          return this.switchMeetingPlanCategory(col);
        case ZOOM_MEETING_SUB_TYPES.ZOOM_ONE_BIZ_PLUS:
        default:
          return this.jumpToOldFlow(col);
      }
    },
    async switchMeetingPlanCategory({ category }) {
      const currentBasePlan = get(this.getCurrentBasePlanInfo, 'plans.0', null);
      const targetPlans = this.planMapFlat.filter(item => item.type === planTypes.ZOOM_MEETING & item.category === category);
      const { max, min } = get(targetPlans, '0', { max: 1, min: 99 });
      let billingCycle = BILLING_CYCLE.ANNUAL;
      let defaultLicenses = min;
      let oldPlanCode = get(currentBasePlan, 'plan_code', '');
      let oldPlanCategory = get(currentBasePlan, 'category', '');
      let oldPlanHost = get(currentBasePlan, 'cartInfo.selected_number', '');
      category && (hostCache[oldPlanCategory] = oldPlanHost);
      if(oldPlanCode) {
        const { billing_cycle, cartInfo } = currentBasePlan;
        billingCycle = billing_cycle;
        if(cartInfo.selected_number < min) {
          // If pro switch to biz default license is min of biz.
          defaultLicenses = min;
        } else if(this.$context.is.edit) {
          defaultLicenses = cartInfo.selected_number;
        } else if(cartInfo.selected_number > max) {
          // If biz switch to pro default license is max of pro.
          defaultLicenses = max;
        }
      }
      const exceedBaseLicensePlans = this.getSelectedPlans.filter(item => (
        item.cartInfo.selected_number > defaultLicenses &&
        item.type !== planTypes.ZOOM_MEETING &&
        this.limitLicenseWithBaseZoneTypes.includes(item.type)
      ));
      const nextMeetingPlan = targetPlans.find(item => item.billing_cycle === billingCycle) || targetPlans[0];
      if(hostCache[category]) {
        defaultLicenses = hostCache[nextMeetingPlan.category];
      }
      if(exceedBaseLicensePlans.length > 0) {
        const planGroupByType = groupBy(exceedBaseLicensePlans, 'type')
        const typeNames = Object.keys(planGroupByType).map(type => {
          const typeNameKey = get(planGroupByType[type], '0.typeNameKey', '');
          return this.$t(typeNameKey);
        });

        // MIX_BUY
        if (!this.$context.is.mixBuy) {
          await this.$confirm(
            this.$t('billing.zone.host_need_adjust.content', [typeNames.join(',')]),
            this.$t('billing.zone.host_need_adjust.title', [typeNames.join(',')]),
            {
              confirmButtonText: this.$t('billing.common.confirm'),
              cancelButtonText: this.$t('billing.common.cancel'),
            }
          );
        }
        exceedBaseLicensePlans.forEach(item => {
          this.updateSelectPlan({
            plan_code: item.plan_code,
            cartInfo: {
              ...item.cartInfo,
              selected_number: defaultLicenses,
            },
          });
        })
      }

      const cartInfo = { selected_number: defaultLicenses };

      await this.switchCategoryAction([oldPlanCode, {...nextMeetingPlan, cartInfo }]);

      this.handleSure();
    },
    jumpToOldFlow({ btn }) {
      location.replace(btn.link);
    },
    getFnByMigrateTypes() {
      return {
        pro2bundle({ col }) {
          return this.jumpToOldFlow(col);
        },
        pro2biz({ col }) {
          return this.switchMeetingPlanCategory(col);
        },
        bizBack2pro({ col }) {
          return this.switchMeetingPlanCategory(col);
        },
        bundleBack2pro() {},
        biz2bundle({ col, currentEditPlanCategory }) {
          // remove pro plan
          if (currentEditPlanCategory === BASE_PLAN_SUB_TYPES.PRO) {
            this.getSelectedPlans.forEach(p => {
              this.removePlan(p.plan_code);
            });
          }
          return this.upgradeToBizPlus(col);
        },
        biz2pro({ col }) {
          this.loadCache();
          return this.switchMeetingPlanCategory(col);
        },
        bundleBack2biz() {
          return this.loadCache();
        },
        proBack2biz({ col }) {
          return this.switchMeetingPlanCategory(col);
        },
        bundle2biz({ col }) {
          return this.bizPlusDowngradeToBiz(col);
        },
        bizBack2bundle() {
          return this.loadCache();
        },
        bundle2pro() {}
      };
    }
  },
}
</script>
<style lang="scss">
@import '@/styles/responsive.scss';

.comparison-dialog {
  .zm-dialog {
    width: 1000px;
  }

  &.comparison-dialog--zoom-one {
    .zm-dialog {
      @include media(mobile) {
        $verticalMargin: 7vh;
        margin-top: $verticalMargin !important;
        margin-bottom: 0 !important;
        height: calc(100% - #{$verticalMargin} * 2) !important;
      }
      @include media(tablet) {
        $verticalMargin: 15vh;
        margin-top: $verticalMargin !important;
        margin-bottom: 0 !important;
        height: calc(100% - #{$verticalMargin} * 2) !important;
      }
      @include media(mobile, tablet) {
        $dialogWidth: calc(100% - 24px);
        width: $dialogWidth !important;
        min-width: $dialogWidth !important;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        &__header {
          flex-shrink: 0;
          width: 100%;
        }
        &__body {
          flex-grow: 1;
          width: 100%;
          overflow: hidden;
        }
      }
    }
  }

  &__title {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 700;
    font-size: 22px;
    line-height: 150%;
    color: #00053D;
    text-align: center;
  }

  .opc-plan-comparison {
    .button-area {
      box-sizing: content-box;
      padding-top: 12px;
      height: 38px;
    }
  }
}

.zm-table th a {
  color: #0e71eb !important;
}

.zm-table--striped .zm-table__body tr.zm-table__row--striped td {
    background: #fff !important;
}

.zm-table__body tr.hover-row.zm-table__row--striped>td, .zm-table__body tr.hover-row>td {
    background-color: #f2f2f7 !important;
}

.opc-comparison-table th a {
    color: #0e71eb !important;
    font-weight: 400;
    text-decoration: none;
}

.zm-table.comparison-table-new{

  .zm-table--striped .zm-table__body tr.zm-table__row--striped td {
    background: #fff;
  }

   .zm-table__body tbody td {
    border-bottom: 1px solid #eaeaf0;
    border-radius: 0;
}

  .feature-col {
    text-align: left;
    font-family: Almaden Sans;
    font-style: normal;
    font-weight: 450;
    font-size: 16px;
    line-height: 150%;
    color: #00053d;
    padding: 20px 0;
}
  .type-title{
    color: #00053D;
  }
  .button-icon{
    background-color: transparent;
    border: none;
    font-size: 14px;
    line-height: 14px;
  }
   .feature-content.sub-title {
    font-family: Almaden Sans;
  }
}

.opc-comparison-table {
  .table-feature-title{
    margin-right: 4px;
  }
}
</style>
